import {
    GraphData,
    Measurements,
    Weather,
    getDeviceReportResponse,
    getCultivationReportResponse,
    Depth,
    getDeviceReportResponseV2,
    NodeInternalWithDetailsTableData,
    DeviceReportTableResponseV2,
    DeviceReportTableV2,
} from '../api'
import { NodeInternalWithDetailsListItem } from '../api/internal_types'
import meteoIcon1 from './icons/meteo/1.svg'
import meteoIcon2 from './icons/meteo/2.svg'
import meteoIcon3 from './icons/meteo/3.svg'
import Plotly from 'react-plotly.js'

//report page
export const getLatestDateInPlot = (
    data: getDeviceReportResponse | getDeviceReportResponseV2 | getCultivationReportResponse
) => {
    const plotData: Plotly.PlotData = Object.assign({}, data?.graphData) as Plotly.PlotData
    if (plotData?.x) {
        const latestPlotDataDate = plotData.x[plotData.x.length - 1]?.toString()
        return latestPlotDataDate
    }
    return undefined
}

//table
export const localeTableSorter =
    <T>(prop: keyof T) =>
    (a: T, b: T) =>
        (a[prop as keyof T] as string).localeCompare(b[prop as keyof T] as string)
export const dateTableSorter =
    <T>(prop: keyof T) =>
    (a: T, b: T) => {
        const dateA = new Date(a[prop as keyof T] as string)
        const dateB = new Date(b[prop as keyof T] as string)
        return dateA.getTime() - dateB.getTime()
    }
export const versionTableSorter =
    <T>(prop: keyof T) =>
    (a: T, b: T) => {
        const partsA = (a[prop as keyof T] as string).split('.').map(Number)
        const partsB = (b[prop as keyof T] as string).split('.').map(Number)
        for (let i = 0; i < Math.max(partsA.length, partsB.length); i++) {
            const diff = (partsA[i] ?? 0) - (partsB[i] ?? 0)
            if (diff !== 0) return diff
        }
        return 0
    }
export const numberTableSorter =
    <T>(prop: keyof T) =>
    (a: T, b: T) => {
        const numA = a[prop as keyof T] as number
        const numB = b[prop as keyof T] as number
        return numA - numB
    }

export const onFilterTableHandler =
    <T>(prop: keyof T) =>
    (value: any, record: T) => {
        return record[prop] === value
    }

export const getTableFilters = <T>(
    data?: T[],
    prop?: keyof T
): { text: string; value: string | number | boolean }[] => {
    if (!data || !prop) {
        return []
    }

    const uniq = [...new Set(data.map((item) => item[prop]))]
    return uniq.sort().map((u) => {
        return {
            text: String(u),
            value: u,
        } as { text: string; value: string | number | boolean }
    })
}

//plot
export const depthDict: { [key: string]: { name: string; color: string } } = {
    [Depth.DEPTH_15]: {
        name: '15cm',
        color: 'blue',
    },
    [Depth.DEPTH_30]: {
        name: '30cm',
        color: 'orange',
    },
    [Depth.DEPTH_45]: {
        name: '45cm',
        color: 'green',
    },
    [Depth.DEPTH_60]: {
        name: '60cm',
        color: 'red',
    },
    [Depth.DEPTH_90]: {
        name: '90cm',
        color: 'purple',
    },
}

export const mapWeatherIcons = (weatherIndex: number): string => {
    switch (weatherIndex) {
        case 0: {
            return meteoIcon1
        }
        case 1: {
            return meteoIcon2
        }
        case 2: {
            return meteoIcon3
        }
        case 3: {
            return meteoIcon1
        }
        default:
            return ''
    }
}

export const rowHeight = 0.05

const getWeatherTextTemplate = (param: keyof Weather): string | undefined => {
    switch (param) {
        case 'temperatureMax':
        case 'temperatureMin': {
            return '%{text:.0f}°'
        }
        case 'windSpeed':
        case 'precipitation': {
            return '%{text:.0f}'
        }
        default: {
            return
        }
    }
}

export const generateWeatherDataAndLayout = (measurements: Measurements) => {
    const { temperatureMax, temperatureMin, precipitation, windSpeed, icons } = measurements.weather

    //weather info
    const weatherInfoDict: { [key: string]: { data: GraphData; label: string; index: number } } = {
        icons: {
            data: icons,
            label: 'weather',
            index: 4,
        },
        precipitation: {
            data: precipitation,
            label: 'mm',
            index: 3,
        },
        temperatureMin: {
            data: temperatureMin,
            label: 'min',
            index: 2,
        },
        temperatureMax: {
            data: temperatureMax,
            label: 'max',
            index: 1,
        },
        windSpeed: {
            data: windSpeed,
            label: 'm/s',
            index: 0,
        },
    }

    const annotations: Plotly.Annotations[] = []
    const yaxes: { [axe: string]: any } = {}
    const images: Partial<Plotly.Image>[] = []

    const weatherInfoData: Plotly.Data[] = Object.keys(weatherInfoDict).map((rowName) => {
        const { data, index, label } = weatherInfoDict[rowName]
        const axeIndex = 2 + index
        const isWeather = label === 'weather'

        const axisdomainStart = index * rowHeight
        annotations.push({
            xref: 'paper',
            yref: 'paper',
            x: -0.03,
            y: axisdomainStart,
            text: label,
            showarrow: false,
        } as Plotly.Annotations)

        yaxes[`yaxis${axeIndex}`] = {
            domain: [axisdomainStart, axisdomainStart + rowHeight],
            showgrid: false,
            visible: false,
        }

        if (isWeather) {
            data.x.forEach((date, i) => {
                images.push({
                    source: mapWeatherIcons(data.y[i] as number),
                    xref: 'x',
                    yref: 'paper',
                    x: date as string,
                    y: axisdomainStart,
                    sizex: 2 * 24 * 60 * 60 * 1000 * 10,
                    sizey: 0.05,
                    xanchor: 'center',
                    yanchor: 'bottom',
                })
            })
        }

        return {
            x: data.x,
            y: Array(data.x.length).fill(0),
            mode: 'text',
            texttemplate: getWeatherTextTemplate(rowName as keyof Weather),
            text: data.y as string[],
            textposition: 'bottom center',
            type: 'scatter',
            textfont: {
                size: isWeather ? 20 : 12,
            },
            showlegend: false,
            showscale: false,
            hoverinfo: 'none',
            yaxis: `y${axeIndex}`,
            visible: !isWeather,
        }
    })

    //layout
    const layout: Partial<Plotly.Layout> = {
        margin: {
            l: 50,
            r: 0,
            b: 0,
            t: 20,
        },
        xaxis: {
            showgrid: false,
            zeroline: false,
        },
        yaxis: {
            domain: [rowHeight * (annotations.length + 1), 1],
            showgrid: false,
            zeroline: false,
        },
        ...yaxes,
        annotations,
        images,
    }

    return {
        weatherLayout: layout,
        weatherInfoData,
    }
}

export const extendBandwidthToPredictionEndDate = (
    bandwidth: { [h: string]: GraphData },
    predictionEndDate: Plotly.Datum | undefined
): { [h: string]: GraphData } => {
    if (!predictionEndDate) {
        return bandwidth
    }

    const newBandwidth: { [h: string]: GraphData } = {}

    Object.keys(bandwidth).forEach((h) => {
        if (!Array.isArray(bandwidth[h].x) || !Array.isArray(bandwidth[h].y)) {
            throw new Error('Invalid data in bandwidth object')
        }
        if (bandwidth[h].y.length === 0) {
            throw new Error('Empty y data in bandwidth object')
        }
        newBandwidth[h] = {
            x: [...bandwidth[h].x, predictionEndDate],
            y: [...bandwidth[h].y, bandwidth[h].y[bandwidth[h].y.length - 1]],
        }
    })

    return newBandwidth
}

export const toKebabCase = (str: string): string => {
    return (
        str
            // 1. convert "camelCase" to "camel-case"
            .replace(/([a-z])([A-Z])/g, '$1-$2')
            // 2. convert "VWCOrEC" to "vwc-or-ec"
            .replace(/([A-Z])([A-Z][a-z])/g, '$1-$2')
            // 3. convert "U1U2" to "U1-U2" (digit-to-uppercase)
            .replace(/([0-9])([A-Z])/g, '$1-$2')
            // Lowercase the entire string
            .toLowerCase()
    )
}

export const convertNanoToMinutes = (nano: number): number => {
    return nano / 60000000000
}

export const convertNanoToHours = (nano: number): number => {
    return nano / 3600000000000
}

export const majorFirmwareVersions = [
    '2.0.18',
    '3.1.4',
    '3.3.0',
    '3.5.0',
    '3.5.3',
    '3.7.8',
    '3.8.5',
    '3.9.2',
    '3.10.0',
    '3.11.0',
]

/**
 * Converts a NodeInternalWithDetailsListItem to NodeInternalWithDetailsTableData format
 * @param item The source item to convert
 * @returns A new object in NodeInternalWithDetailsTableData format
 */
export const convertToNodeTableData = (
    item: NodeInternalWithDetailsListItem & {
        node: NonNullable<NodeInternalWithDetailsListItem['node']>
        lastMeasurement?: NonNullable<NodeInternalWithDetailsListItem['lastMeasurement']>
    }
): NodeInternalWithDetailsTableData => {
    const { id, farm, cultivation, node, lastMeasurement } = item
    const geoLocation = 'geoLocation' in node ? node.geoLocation : undefined
    const overrideGeoLocation = 'overrideGeoLocation' in node ? node.overrideGeoLocation : undefined
    const configuration = 'configuration' in node ? node.configuration : undefined

    return {
        nodeId: id,
        // Farm details
        farmName: farm?.name || '',
        farmId: farm?.id || '',
        projectName: farm?.project?.name || '',
        // Cultivation details
        cultivationName: cultivation?.name || '',
        cultivationId: cultivation?.id || '',
        // Node details
        battery: node.battery,
        inclination: node.inclination,
        firmwareVersion: node.firmwareVersion,
        // Geo location
        latitude: geoLocation?.latitude,
        longitude: geoLocation?.longitude,
        overrideLatitude: overrideGeoLocation?.latitude,
        overrideLongitude: overrideGeoLocation?.longitude,
        // Target Configuration
        targetAcquisitionPeriod: configuration?.target?.acquisitionPeriod,
        targetTransmissionPeriod: configuration?.target?.transmissionPeriod,
        targetConfigUpdatedAt: configuration?.target?.updatedAt,
        // Current Configuration
        currentAcquisitionPeriod: configuration?.current?.acquisitionPeriod,
        currentTransmissionPeriod: configuration?.current?.transmissionPeriod,
        currentConfigUpdatedAt: configuration?.current?.updatedAt,
        // Last measurement
        lastMeasurementTime: lastMeasurement?.dateTime,
        lastAirTemperature: lastMeasurement?.airTemperature,
        lastAirHumidity: lastMeasurement?.airHumidity,
        lastCalibrationModelNameVwc: lastMeasurement?.calibrationModelNameVwc || '',
        lastCalibrationModelNameEc: lastMeasurement?.calibrationModelNameEc || '',
        // Additional flags
        hasGeoLocation: !!geoLocation,
        hasOverrideLocation: !!overrideGeoLocation,
        hasConfiguration: !!configuration,
        hasLastMeasurement: !!lastMeasurement,
    }
}

/**
 * Converts a DeviceReportTableResponseV2 to DeviceReportTableV2 format
 * @param response The response data to convert
 * @returns A new DeviceReportTableV2 object with mapped properties
 */
export const convertToDeviceReportTableV2 = (response: DeviceReportTableResponseV2): DeviceReportTableV2 => {
    const { lastChanged, comment, status, skipReasons, badReasons } = response

    // First convert the common fields using convertToNodeTableData
    const baseData = convertToNodeTableData({
        ...response,
        node: response.node, // This ensures TypeScript knows node is defined
        lastMeasurement: response.lastMeasurement,
    })

    // Then add the additional fields specific to DeviceReportTableV2
    return {
        ...baseData,
        // DeviceReportTableV2CommonStatus fields
        lastChanged: lastChanged,
        comment,

        // DeviceReportTableBadSoilContactV3Result fields
        status,
        skipReasons,
        badReasons,
    }
}

/**
 * Explains the reason for a bad soil contact status
 * @param reason The reason code to explain
 * @returns A human-readable explanation of the reason
 */
export const explainBadReason = (reason: string): string => {
    switch (reason) {
        case 'rapid_moisture_loss':
            return 'Recent rapid moisture loss detected.'
        case 'insufficient_moisture':
            return 'The VWC measurements are too low at a certain depth.'
        case 'moisture_gradient_anomaly':
            return 'The VWC is significantly lower at lower depths compared to higher depths.'
        case 'reduced_surface_moisture_response':
            return "The surface liquid's response is worse than at deeper levels."
        case 'moisture_loss_by_installation':
            return 'A significant moisture loss by a recent re-installation is detected.'
        case 'too_low_surface_vwc_ec':
            return 'The VWC and EC measurements at the surface are too low.'
        case 'too_low_surface_vwc_since_first_installation':
            return 'The VWC at the surface is too low since the first installation.'
        default:
            return reason
    }
}
